import { useEffect } from 'react';

import { copy, localeNames, supportedLocales } from './i18n.js';
import { contentPageKeys, getContentPage, getContentSources } from './contentPages.js';
import { contact, getLegalDocument, getLegalUpdatedDate } from './legalContent.js';
import { getRoutePath, parseRoute } from './routes.js';
import { APP_STORE_URL, GOOGLE_PLAY_URL, seoCopy, SITE_URL } from './seo.js';

function Icon({ name, size = 22 }) {
  const paths = {
    arrow: <><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></>,
    back: <><path d="M19 12H5"/><path d="m11 18-6-6 6-6"/></>,
    camera: <><path d="M14.5 4 16 7h3a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h3l1.5-3z"/><circle cx="12" cy="13" r="3.25"/></>,
    check: <path d="m5 12 4 4L19 6"/>,
    download: <><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></>,
    file: <><path d="M6 2h8l4 4v16H6z"/><path d="M14 2v5h5"/><path d="M9 13h6"/><path d="M9 17h6"/></>,
    globe: <><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a15 15 0 0 1 0 18"/><path d="M12 3a15 15 0 0 0 0 18"/></>,
    image: <><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9" r="1.5"/><path d="m4 17 5-5 4 4 2-2 5 5"/></>,
    lock: <><rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></>,
    sparkles: <><path d="m12 3 1.2 3.8L17 8l-3.8 1.2L12 13l-1.2-3.8L7 8l3.8-1.2z"/><path d="m6 14 .8 2.2L9 17l-2.2.8L6 20l-.8-2.2L3 17l2.2-.8z"/><path d="m18 13 .7 1.7 1.8.8-1.8.7L18 18l-.7-1.8-1.8-.7 1.8-.8z"/></>,
    wallet: <><path d="M4 6a2 2 0 0 1 2-2h12v16H6a2 2 0 0 1-2-2z"/><path d="M4 8h14"/><path d="M15 13h3"/></>,
  };

  return (
    <svg className="icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M17.7 12.6c0-2.5 2.1-3.7 2.2-3.8a4.8 4.8 0 0 0-3.8-2.1c-1.6-.2-3.2 1-4 1-1 0-2.4-1-3.8-1-2 0-3.9 1.2-4.9 3-2.1 3.6-.5 9 1.5 12 .9 1.4 2 2.8 3.5 2.8 1.4-.1 2-1 3.7-1 1.7 0 2.2 1 3.7 1 1.5 0 2.5-1.4 3.4-2.7 1-1.6 1.5-3.1 1.6-3.2-.1 0-3.1-1.2-3.1-4Zm-2.6-7.7a4.5 4.5 0 0 0 1-3.3A4.7 4.7 0 0 0 13 3.2a4.2 4.2 0 0 0-1.1 3.2 3.9 3.9 0 0 0 3.2-1.5Z"/>
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#34A853" d="M3.4 2.7 14 12 3.4 21.3a2.2 2.2 0 0 1-.4-1.2V3.9c0-.5.1-.9.4-1.2Z"/>
      <path fill="#4285F4" d="m14 12 3.4-3-10.8-6a3 3 0 0 0-2.7-.4Z"/>
      <path fill="#FBBC04" d="m14 12-10.1 9.4a3 3 0 0 0 2.7-.4l10.8-6Z"/>
      <path fill="#EA4335" d="M21 11 17.4 9 14 12l3.4 3 3.6-2c.9-.5.9-1.5 0-2Z"/>
    </svg>
  );
}

function LanguageSwitcher({ locale, setLocale, label }) {
  return (
    <label className="language-switcher">
      <Icon name="globe" size={17} />
      <span className="sr-only">{label}</span>
      <select value={locale} onChange={(event) => setLocale(event.target.value)} aria-label={label}>
        {supportedLocales.map((code) => <option key={code} value={code}>{localeNames[code]}</option>)}
      </select>
    </label>
  );
}

function StoreButton({ store, text }) {
  const isApple = store === 'apple';
  return (
    <a className="store-button" href={isApple ? APP_STORE_URL : GOOGLE_PLAY_URL} target="_blank" rel="noreferrer">
      <span className="store-icon">{isApple ? <AppleIcon /> : <PlayIcon />}</span>
      <span><small>{isApple ? text.appStoreOverline : text.googlePlayOverline}</small><strong>{isApple ? text.appStore : text.googlePlay}</strong></span>
    </a>
  );
}

function Header({ locale, setLocale, text, legal = false }) {
  const homePath = getRoutePath(locale);

  return (
    <header className="site-header">
      <div className="container header-inner">
        <a className="brand" href={homePath} aria-label="FotoPobyt">
          <img src="/images/app-icon-96.png" alt="" width="96" height="96" />
          <span>FotoPobyt</span>
        </a>
        {!legal && (
        <nav className="main-nav">
            <a href="#how">{text.navHow}</a>
            <a href="#benefits">{text.navBenefits}</a>
            <a href="#faq">{text.navFaq}</a>
            <a href={getRoutePath(locale, 'guides')}>{text.navGuides}</a>
          </nav>
        )}
        <div className="header-actions">
          <LanguageSwitcher locale={locale} setLocale={setLocale} label={text.language} />
          <a className="header-cta" href={legal ? `${homePath}#download` : '#download'}>{text.navDownload}<Icon name="arrow" size={17}/></a>
        </div>
      </div>
    </header>
  );
}

function Footer({ locale, text }) {
  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div>
          <a className="brand footer-brand" href={getRoutePath(locale)}><img src="/images/app-icon-96.png" alt="" width="96" height="96" /><span>FotoPobyt</span></a>
          <p>{text.footerTagline}</p>
        </div>
        <div className="footer-links">
          <a href={getRoutePath(locale, 'guides')}>{text.navGuides}</a>
          <a href={getRoutePath(locale, 'about')}>{text.about}</a>
          <a href={getRoutePath(locale, 'support')}>{text.supportPage}</a>
          <a href={getRoutePath(locale, 'privacy')}>{text.privacy}</a>
          <a href={getRoutePath(locale, 'terms')}>{text.terms}</a>
          <a href={`mailto:${contact.email}`}>{text.support}</a>
        </div>
      </div>
      <div className="container footer-bottom">
        <span>{text.copyright}</span>
        <span>{text.contactLocation}</span>
      </div>
    </footer>
  );
}

function HomePage({ locale, setLocale, text }) {
  const steps = [
    { icon: 'camera', title: text.step1Title, body: text.step1Body },
    { icon: 'sparkles', title: text.step2Title, body: text.step2Body },
    { icon: 'download', title: text.step3Title, body: text.step3Body },
  ];
  const benefits = [
    { icon: 'wallet', title: text.benefit1Title, body: text.benefit1Body },
    { icon: 'file', title: text.benefit2Title, body: text.benefit2Body },
    { icon: 'lock', title: text.benefit3Title, body: text.benefit3Body },
    { icon: 'globe', title: text.benefit4Title, body: text.benefit4Body },
  ];
  const faqs = [[text.faq1Q, text.faq1A], [text.faq2Q, text.faq2A], [text.faq3Q, text.faq3A], [text.faq4Q, text.faq4A]];

  return (
    <>
      <Header locale={locale} setLocale={setLocale} text={text} />
      <main>
        <section className="hero">
          <div className="container hero-grid">
            <div className="hero-copy">
              <div className="eyebrow"><span className="eyebrow-dot" />{text.eyebrow}</div>
              <h1>{text.heroTitle}</h1>
              <p className="hero-subtitle">{text.heroSubtitle}</p>
              <div className="store-row"><StoreButton store="apple" text={text}/><StoreButton store="google" text={text}/></div>
              <p className="hero-note"><Icon name="check" size={16}/>{text.heroNote}</p>
            </div>
            <div className="hero-visual">
              <div className="visual-glow" />
              <picture>
                <source type="image/avif" srcSet="/images/hero-photo.avif" />
                <source type="image/webp" srcSet="/images/hero-photo-900.webp 900w, /images/hero-photo-1400.webp 1400w, /images/hero-photo.webp 1800w" sizes="(max-width: 980px) 100vw, 52vw" />
                <img src="/hero-photo.jpg" alt={text.heroAlt} width="1800" height="1004" fetchPriority="high" decoding="async" />
              </picture>
              <div className="floating-card floating-format"><Icon name="image" size={19}/><strong>684 × 883</strong><span>JPG</span></div>
              <div className="floating-card floating-ready"><span className="ready-icon"><Icon name="check" size={16}/></span>{text.proofPreview}</div>
            </div>
          </div>
          <div className="container proof-row">
            <div><strong>684 × 883</strong><span>{text.proofFormat}</span></div>
            <div><strong>0 zł</strong><span>{text.proofPreview}</span></div>
            <div><strong>JPG</strong><span>{text.proofPhone}</span></div>
          </div>
        </section>

        <section className="section how-section" id="how">
          <div className="container">
            <div className="section-heading centered"><span className="section-kicker">{text.howEyebrow}</span><h2>{text.howTitle}</h2><p>{text.howSubtitle}</p></div>
            <div className="steps-grid">
              {steps.map((step, index) => <article className="step-card" key={step.title}><span className="step-number">0{index + 1}</span><span className="icon-box"><Icon name={step.icon}/></span><h3>{step.title}</h3><p>{step.body}</p></article>)}
            </div>
            <div className="process-image"><picture><source type="image/avif" srcSet="/images/how-it-works.avif"/><source type="image/webp" srcSet="/images/how-it-works-700.webp 700w, /images/how-it-works-1100.webp 1100w" sizes="(max-width: 720px) 100vw, 920px"/><img src="/how-it-works.jpg" alt={text.processAlt} width="1400" height="1400" loading="lazy" decoding="async"/></picture></div>
          </div>
        </section>

        <section className="section benefits-section" id="benefits">
          <div className="container benefits-layout">
            <div className="benefits-intro">
              <span className="section-kicker">{text.benefitsEyebrow}</span><h2>{text.benefitsTitle}</h2>
              <div className="checklist-card"><h3>{text.checklistTitle}</h3>{text.checklistItems.map((item) => <div className="checklist-item" key={item}><span><Icon name="check" size={15}/></span>{item}</div>)}</div>
            </div>
            <div className="benefits-grid">{benefits.map((benefit) => <article className="benefit-card" key={benefit.title}><span className="icon-box"><Icon name={benefit.icon}/></span><h3>{benefit.title}</h3><p>{benefit.body}</p></article>)}</div>
          </div>
        </section>

        <section className="section faq-section" id="faq">
          <div className="container faq-layout">
            <div className="section-heading"><span className="section-kicker">{text.faqEyebrow}</span><h2>{text.faqTitle}</h2></div>
            <div className="faq-list">{faqs.map(([question, answer], index) => <details key={question} open={index === 0}><summary>{question}<span>+</span></summary><p>{answer}</p></details>)}</div>
          </div>
        </section>

        <section className="section guides-preview-section" aria-labelledby="guides-preview-title">
          <div className="container">
            <div className="section-heading centered"><span className="section-kicker">{text.guidesEyebrow}</span><h2 id="guides-preview-title">{text.guidesTitle}</h2><p>{text.guidesSubtitle}</p></div>
            <div className="guide-preview-grid">
              {getContentPage('guides', locale).content.cards.slice(0, 4).map(([key, title, description]) => (
                <a className="guide-preview-card" href={getRoutePath(locale, key)} key={key}>
                  <span className="guide-preview-index">{String(getContentPage('guides', locale).content.cards.findIndex(([cardKey]) => cardKey === key) + 1).padStart(2, '0')}</span>
                  <h3>{title}</h3>
                  <p>{description}</p>
                  <span className="guide-preview-link">{text.readGuide}<Icon name="arrow" size={16}/></span>
                </a>
              ))}
            </div>
            <a className="text-link" href={getRoutePath(locale, 'guides')}>{text.guidesMore}<Icon name="arrow" size={16}/></a>
          </div>
        </section>

        <section className="download-section" id="download">
          <div className="container download-card">
            <div className="download-copy"><img src="/images/app-icon-96.png" alt="" width="96" height="96" loading="lazy"/><div><h2>{text.ctaTitle}</h2><p>{text.ctaBody}</p></div></div>
            <div className="store-row"><StoreButton store="apple" text={text}/><StoreButton store="google" text={text}/></div>
          </div>
        </section>
      </main>
      <Footer locale={locale} text={text}/>
    </>
  );
}

function LegalPage({ type, locale, setLocale, text }) {
  const document = getLegalDocument(locale, type);
  const updated = getLegalUpdatedDate(locale);

  return (
    <>
      <Header locale={locale} setLocale={setLocale} text={text} legal />
      <main className="legal-main">
        <div className="container legal-header">
          <a className="back-link" href={getRoutePath(locale)}><Icon name="back" size={18}/>{text.legalBack}</a>
          <span className="section-kicker">FotoPobyt</span>
          <h1>{document.title}</h1>
          <p className="legal-updated">{text.lastUpdated}: {updated}</p>
          <p className="legal-lead">{document.intro}</p>
        </div>
        <div className="container legal-layout">
          <aside className="legal-toc">
            <strong>{text.legalToc}</strong>
            <nav>{document.sections.map((section, index) => <a key={section.title} href={`#section-${index + 1}`}>{section.title}</a>)}</nav>
          </aside>
          <article className="legal-article">
            {document.sections.map((section, index) => (
              <section key={section.title} id={`section-${index + 1}`}>
                <h2>{section.title}</h2>
                {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                {section.bullets && <ul>{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>}
              </section>
            ))}
            <div className="legal-contact">
              <span className="icon-box"><Icon name="file"/></span>
              <div><strong>{text.legalDisclaimer}</strong><p>{contact.name}<br/><a href={`mailto:${contact.email}`}>{contact.email}</a><br/>{text.contactLocation}</p></div>
            </div>
          </article>
        </div>
      </main>
      <Footer locale={locale} text={text}/>
    </>
  );
}

function formatContentDate(value, locale) {
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${value}T00:00:00Z`));
}

function ArticleCta({ text }) {
  return (
    <section className="article-cta" aria-labelledby="article-cta-title">
      <div className="article-cta-copy"><img src="/images/app-icon-96.png" alt="" width="96" height="96" loading="lazy"/><div><h2 id="article-cta-title">{text.articleCtaTitle}</h2><p>{text.articleCtaBody}</p></div></div>
      <div className="store-row"><StoreButton store="apple" text={text}/><StoreButton store="google" text={text}/></div>
    </section>
  );
}

function GuideIndexPage({ locale, setLocale, text }) {
  const page = getContentPage('guides', locale);
  return (
    <>
      <Header locale={locale} setLocale={setLocale} text={text} legal />
      <main className="content-main">
        <div className="container content-header">
          <a className="back-link" href={getRoutePath(locale)}><Icon name="back" size={18}/>{text.legalBack}</a>
          <span className="section-kicker">{text.guidesEyebrow}</span>
          <h1>{page.content.heading}</h1>
          <p className="content-lead">{page.content.intro}</p>
        </div>
        <div className="container guide-index-grid">
          {page.content.cards.map(([key, title, description], index) => (
            <a className="guide-card" href={getRoutePath(locale, key)} key={key}>
              <span className="guide-card-number">0{index + 1}</span>
              <h2>{title}</h2>
              <p>{description}</p>
              <span className="text-link">{text.readGuide}<Icon name="arrow" size={16}/></span>
            </a>
          ))}
        </div>
        <div className="container content-cta-wrap"><ArticleCta text={text}/></div>
      </main>
      <Footer locale={locale} text={text}/>
    </>
  );
}

function ContentPage({ pageKey, locale, setLocale, text }) {
  const page = getContentPage(pageKey, locale);
  const sources = getContentSources(page, locale);
  const isArticle = page.kind === 'article';
  const related = isArticle
    ? contentPageKeys.filter((key) => ['requirements', 'mos', 'phone', 'mistakes'].includes(key) && key !== pageKey)
    : [];

  return (
    <>
      <Header locale={locale} setLocale={setLocale} text={text} legal />
      <main className="content-main">
        <div className="container content-header">
          <nav className="breadcrumbs" aria-label={text.legalToc}>
            <a href={getRoutePath(locale)}>{seoCopy[locale].homeLabel}</a><span>/</span>
            <a href={getRoutePath(locale, 'guides')}>{getContentPage('guides', locale).content.heading}</a><span>/</span>
            <span>{page.content.heading}</span>
          </nav>
          <span className="section-kicker">FotoPobyt</span>
          <h1>{page.content.heading}</h1>
          <p className="content-lead">{page.content.intro}</p>
          <p className="content-updated">{text.lastUpdated}: {formatContentDate(page.updatedAt, locale)}</p>
        </div>
        <div className="container content-layout">
          <article className="content-article">
            {page.content.sections.map((section) => (
              <section key={section.heading}>
                <h2>{section.heading}</h2>
                {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                {section.bullets && <ul>{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>}
              </section>
            ))}
            {sources.length > 0 && (
              <section className="content-sources">
                <h2>{text.sourcesTitle}</h2>
                <p>{formatContentDate(page.updatedAt, locale)}</p>
                <ul>{sources.map((source) => <li key={source.url}><a href={source.url} target="_blank" rel="noreferrer">{source.label}</a></li>)}</ul>
              </section>
            )}
            {related.length > 0 && (
              <section className="related-guides">
                <h2>{text.relatedTitle}</h2>
                <div className="related-guide-list">{related.map((key) => { const relatedPage = getContentPage(key, locale); return <a href={getRoutePath(locale, key)} key={key}>{relatedPage.content.heading}<Icon name="arrow" size={16}/></a>; })}</div>
              </section>
            )}
            {isArticle && <ArticleCta text={text}/>} 
          </article>
        </div>
      </main>
      <Footer locale={locale} text={text}/>
    </>
  );
}

function NotFoundPage({ locale, setLocale, text }) {
  return (
    <>
      <Header locale={locale} setLocale={setLocale} text={text} legal />
      <main className="not-found-main"><div className="container not-found-card"><span className="section-kicker">FotoPobyt</span><h1>{text.notFoundTitle}</h1><p>{text.notFoundBody}</p><a className="header-cta" href={getRoutePath(locale)}>{text.notFoundButton}<Icon name="arrow" size={17}/></a></div></main>
      <Footer locale={locale} text={text}/>
    </>
  );
}

export function FotoPobytPage({ locale, type, setLocale }) {
  const text = copy[locale] ?? copy.en;

  if (type === 'privacy' || type === 'terms') return <LegalPage type={type} locale={locale} setLocale={setLocale} text={text}/>;
  if (type === 'guides') return <GuideIndexPage locale={locale} setLocale={setLocale} text={text}/>;
  if (contentPageKeys.includes(type)) return <ContentPage pageKey={type} locale={locale} setLocale={setLocale} text={text}/>;
  return <HomePage locale={locale} setLocale={setLocale} text={text}/>;
}

export default function App() {
  const route = parseRoute(window.location.pathname);
  const locale = route.locale;
  const type = route.type;
  const text = copy[locale] ?? copy.en;

  function setLocale(nextLocale) {
    window.localStorage.setItem('fotopobyt-locale', nextLocale);
    window.location.assign(`${getRoutePath(nextLocale, type)}${window.location.hash}`);
  }

  useEffect(() => {
    const legacyLocale = new URLSearchParams(window.location.search).get('lang');
    if (supportedLocales.includes(legacyLocale) && legacyLocale !== locale) {
      window.location.replace(`${getRoutePath(legacyLocale, type)}${window.location.hash}`);
      return;
    }

    document.documentElement.lang = locale;
    const page = type && contentPageKeys.includes(type) ? getContentPage(type, locale) : null;
    const title = page ? page.content.title : type ? `${getLegalDocument(locale, type).title} — FotoPobyt` : seoCopy[locale].homeTitle;
    const description = type
      ? page ? page.content.description : type === 'privacy' ? seoCopy[locale].privacyDescription : seoCopy[locale].termsDescription
      : seoCopy[locale].homeDescription;
    document.title = title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', description);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${SITE_URL}${getRoutePath(locale, type)}`);
  }, [locale, text, type]);

  if (!route.known) return <NotFoundPage locale={locale} setLocale={setLocale} text={text}/>;
  return <FotoPobytPage locale={locale} type={type} setLocale={setLocale}/>;
}
